import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  NgZone,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  LPR_AXIS_ENTRY_UPDATED_SSE_EVENT,
  TUNNEL_WASH_STATE_SSE_EVENT,
  VEHICLE_WASHES_SSE_EVENT,
} from '../shared/constants/realtime.constants';
import { RealtimeEventsService } from '../services/realtime-events.service';
import { OrderDetailsModalComponent } from '../components/order-details-modal/order-details-modal.component';
import {
  findLatestTunnelRowForOrder,
  isSameTunnelRow,
  tunnelRowKey,
} from './utils/tunnel-row.util';
import {
  TunnelWashRowDisplay,
  applyTunnelWashState,
  describeTunnelWashRow,
  RecordedTunnelWashEvent,
  indexAfterRemoval,
  isTunnelWashExpired,
  mapTunnelWashFieldsFromApi,
  needsTunnelWashClock,
  parseTunnelWashStateEvent,
  reapplyTunnelWashEvents,
} from './utils/tunnel-wash.util';
import { TunnelWashSummaryComponent } from './components/tunnel-wash-summary/tunnel-wash-summary.component';
import { OrdersService } from './services/orders.service';
import { ApiService } from '../services/api.service';
import { NotificationService } from '../services/notification.service'; // NotificationService;
import { formatDateTimeWithTimezone, formatLocalDateTime } from '../shared/utils/date.util';
import { environment } from '../../environments/environment';
import {
  isThisTabImpersonating,
  readActiveTunnelData,
  removeImpersonationStorageItem,
  IMPERSONATION_TUNNEL_DATA_KEY,
} from '../shared/utils/impersonation-handoff.util';
import { ImpersonationService } from '../admin/impersonation/services/impersonation.service';
import { Order, OrderStatus, OrderFilters } from './models/order.models';
import {
  PAYMENT_MODE_FILTER_CATEGORY,
  PAYMENT_MODE_FILTER_OPTIONS,
  CAR_WASH_TYPE_FILTER_OPTIONS,
  createDefaultAdminOrderFilters,
  PaymentModeApiFilterItem,
} from './constants/orders.constants';
import {
  convertCategoriesToOrderFilters as convertSharedCategoriesToOrderFilters,
  isOptionAppliedFromOrderFilters,
  countActiveOrderFilters,
  syncOrderPaymentModeFilterCategories,
  applyOrderTerminalPaymentFilterVisibility,
} from './utils/orders-page-shared.utils';
import { EmptyResultComponent } from '../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { SearchbarComponent } from '../shared/components/searchbar/searchbar.component';
import { ActionButtonComponent } from '../shared/components/action-button/action-button.component';
import { FilterModalComponent } from '../shared/components/filter-modal/filter-modal.component';
import { FilterCategory } from '../shared/components/filter-modal/filter-modal.component';
import { LoggerService } from '../services/logger.service';
import { OrderUtils } from './utils/order.utils';
import { resolveVehicleWashHighlight } from './utils/vehicle-wash-highlight.util';
import { PaymentMethodCode } from '../shared/types/payment-method.enum';
import {
  isUpdatedPusherPayload,
  resolveLprEntryIdFromNotificationData,
} from '../notifications/notification.utils';
import { getLicensePlateNumberFromNotification } from '../shared/utils/notification-display.util';

@Component({
  selector: 'app-tunnel-orders',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    OrderDetailsModalComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    SearchbarComponent,
    ActionButtonComponent,
    FilterModalComponent,
    TunnelWashSummaryComponent,
  ],
  templateUrl: './tunnel-orders.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tunnel-orders.component.scss'],
})
export class TunnelOrdersComponent implements OnInit, OnDestroy {
  private static readonly TUNNEL_AMPLITUDE_SESSION_REPLAY_API_KEY =
    '3cf22a0c6d970c9082ce2bbc976d590d';
  private static readonly TUNNEL_AMPLITUDE_SCRIPT_ID = 'way-tunnel-amplitude-session-replay-sdk';
  private static readonly TUNNEL_AMPLITUDE_SCRIPT_SRC =
    'https://cdn.amplitude.com/script/3cf22a0c6d970c9082ce2bbc976d590d.js';

  searchTerm = '';
  loading = false;

  selectedLocationId = '';

  orderStatuses: OrderStatus[] = [];
  orders: Order[] = [];

  showFilterModal = false;

  readonly filterCategories = signal<FilterCategory[]>([
    {
      id: 'carWashType',
      name: 'Car Wash Type',
      options: CAR_WASH_TYPE_FILTER_OPTIONS.map(option => ({ ...option })),
    },
    {
      id: 'status',
      name: 'Status',
      options: [
        { id: 'completed', label: 'Completed', value: 'completed', selected: false },
        { id: 'scheduled', label: 'Scheduled', value: 'scheduled', selected: false },
        { id: 'canceled', label: 'Canceled', value: 'canceled', selected: false },
        { id: 'in-progress', label: 'In Progress', value: 'inProgress', selected: false },
      ],
    },
    {
      id: 'source',
      name: 'Order Source',
      options: [
        { id: 'way-com', label: 'Way.com', value: 'wayCom', selected: false },
        { id: 'way-app', label: 'Way App', value: 'wayApp', selected: false },
        { id: 'website', label: 'Website', value: 'website', selected: false },
      ],
    },
    {
      ...PAYMENT_MODE_FILTER_CATEGORY,
      options: PAYMENT_MODE_FILTER_OPTIONS.map(option => ({ ...option })),
    },
  ]);

  shimmerColumns: ShimmerColumn[] = [
    { label: 'Order ID', width: 'sm' },
    { label: 'Status', width: 'sm' },
    { label: 'Date & Time', width: 'md' },
    { label: 'Customer', width: 'md' },
    { label: 'Wash Type', width: 'sm' },
    { label: 'Amount', width: 'sm' },
  ];

  appliedFilters: OrderFilters = createDefaultAdminOrderFilters();

  showOrderDetailsModal = false;
  selectedOrder: Order | null = null;

  // Currently selected card in the card list view
  selectedCardOrder: Order | null = null;

  /** How long the undo toast stays visible after a successful soft-delete API call. */
  private static readonly DELETE_UNDO_MS = 8000;
  private static readonly TUNNEL_LIST_REFRESH_DELAY_MS = 2000;
  private static readonly SLOW_TUNNEL_DISPLAY_MS = 2000;

  deleteUndoToast: { licensePlate: string } | null = null;

  /**
   * CWS-1921 tunnel wash state. `nowMs` is the board clock (ticks every second while a
   * row is `sent` or `pending`) behind the linger countdown and the 15 s "no
   * confirmation" notice; `tunnelWashRows` holds each row's computed display.
   */
  nowMs = Date.now();
  private tunnelWashRows = new Map<string, TunnelWashRowDisplay>();
  private tunnelWashClock: ReturnType<typeof setInterval> | null = null;
  private static readonly TUNNEL_WASH_CLOCK_MS = 1000;
  /**
   * List requests are numbered so an older response never replaces a newer one, and
   * wash events are kept until a response requested after them has been applied
   * (CWS-1921 review: an in-flight list must not revert a newer event).
   */
  private fetchSeq = 0;
  private appliedFetchSeq = 0;
  private recentTunnelWashEvents: RecordedTunnelWashEvent[] = [];
  private static readonly MAX_RECENT_TUNNEL_WASH_EVENTS = 100;
  private pendingDelete: {
    order: Order;
    index: number;
    undoTimeoutId: ReturnType<typeof setTimeout>;
  } | null = null;
  private isDeleteApiInFlight = false;

  // Angular 22 runs this app with zoneless-style change detection: a plain field
  // mutated inside an HTTP/SSE callback no longer repaints on its own (verified —
  // after the orders request resolved the component held `loading === false` and
  // the fetched rows while the DOM still showed the loading shimmer, until any
  // template event marked the view dirty). Every async state change in this
  // component must therefore notify Angular explicitly.
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ordersService = inject(OrdersService);
  private readonly apiService = inject(ApiService);
  private readonly messageService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);
  private readonly ngZone = inject(NgZone);
  private readonly realtime = inject(RealtimeEventsService);
  private readonly impersonation = inject(ImpersonationService);

  /** Remote (BetterStack): structured tunnel events + full UI snapshot (aligned with NotificationModal). */
  private readonly tunnelLogSource = 'TunnelOrders';

  /** Monotonic clock for `timingMsSincePageOpen` in tunnel logs. */
  private pageOpenTime = 0;

  private readonly RHOMBUS_API_KEY = environment.rhombusApiKey;
  private rhombusImageCache = new Map<string, string>();

  /** Blob URL after authenticated fetch (Rhombus frame requires API key; not usable as a bare img src). */
  rhombusImageUrl: string | null = null;
  rhombusImageLoading = false;
  rhombusImageError = false;

  /** Direct public URL from API `image_s3_url` (all sources including axis/LPR). */
  nonWebflowDetailImageUrl: string | null = null;
  nonWebflowDetailImageLoading = false;
  nonWebflowDetailImageError = false;

  /**
   * Tracks which order's image is currently loading/displayed.
   * Used to discard stale (error)/(load) events that fire on the DOM
   * element after the selected order has already changed.
   */
  private nonWebflowImageOrderId: string | null = null;

  /** Original URL kept for retries when the image fails to load. */
  private nonWebflowImageRetryUrl: string | null = null;
  private nonWebflowRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly NONWEBFLOW_RETRY_INTERVAL_MS = 5000;

  private destroy$ = new Subject<void>();
  private sseSub: Subscription | null = null;
  private ownsSseConnection = false;
  newOrderId: string | null = null;
  private vehicleWashTimingByOrderId = new Map<
    string,
    { paidAtMs: number; vehicleWashesReceivedAtMs: number }
  >();

  /** When the tab was hidden (sleep / app switch); used to refresh after meaningful background time. */
  private visibilityHiddenAt: number | null = null;
  private resumeRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly resumeRefreshDebounceMs = 400;
  /** Built-in payment mode filter options only — tunnel must not call `user/payment-modes`. */
  private filterPaymentModes: PaymentModeApiFilterItem[] = [];
  /** Show Terminal Payment filter only when the tunnel location has linked readers. */
  private showTerminalPaymentFilter = false;
  /** Ignore visibility blips shorter than this (still refresh after real sleep). */
  private readonly minHiddenMsForResumeRefresh = 800;
  private readonly onVisibilityChangeBound = (): void => this.onDocumentVisibilityChange();
  private readonly onWindowOnlineBound = (): void => this.onWindowOnline();

  /** Remote (BetterStack): one interaction + serializable snapshot (NotificationModal pattern). */
  private logTunnelInteraction(action: string, detail?: Record<string, any>): void {
    this.logger.info(
      `[TunnelOrders] ${action}`,
      { action, ...detail, snapshot: this.getTunnelSnapshot() },
      this.tunnelLogSource
    );
  }

  private getTunnelSnapshot(): Record<string, any> {
    const sel = this.selectedCardOrder;
    return {
      locationId: this.selectedLocationId || null,
      search: this.searchTerm.trim()
        ? { length: this.searchTerm.length, preview: this.searchTerm.trim().slice(0, 120) }
        : null,
      list: {
        ordersCount: this.orders.length,
        loading: this.loading,
      },
      selection: sel
        ? {
            id: sel.id,
            orderId: sel.orderId,
            licensePlate: sel.licensePlate,
            event: sel.event ?? null,
          }
        : null,
      orderDetailsModal: {
        open: this.showOrderDetailsModal,
        orderId: this.selectedOrder?.orderId ?? null,
      },
      newOrderHighlightId: this.newOrderId,
      filters: {
        applied: this.appliedFilters,
        activeFilterCount: this.getActiveFilterCount(),
        hasSearchOrFilters: this.hasSearchOrFilters,
      },
      images: {
        rhombusLoading: this.rhombusImageLoading,
        rhombusError: this.rhombusImageError,
        hasRhombusBlobUrl: !!this.rhombusImageUrl,
        nonWebflowLoading: this.nonWebflowDetailImageLoading,
        nonWebflowError: this.nonWebflowDetailImageError,
      },
      ui: {
        showFilterModal: this.showFilterModal,
        deleteUndoToastVisible: !!this.deleteUndoToast,
        pendingDeleteOrderId: this.pendingDelete?.order.id ?? null,
        isDeleteApiInFlight: this.isDeleteApiInFlight,
      },
      sse: {
        subscribed: !!this.sseSub,
        ownsConnection: this.ownsSseConnection,
        connected: this.realtime.isConnected(),
      },
      timingMsSincePageOpen: this.pageOpenTime ? Date.now() - this.pageOpenTime : undefined,
    };
  }

  private summarizeRealtimePayload(data: any): Record<string, any> {
    if (data == null) return { type: 'null' };
    if (typeof data !== 'object') return { value: String(data).slice(0, 200) };
    const o = data as Record<string, any>;
    const summary: Record<string, any> = { keys: Object.keys(o) };
    if (o['order_id'] != null) summary['order_id'] = o['order_id'];
    if (o['orderId'] != null) summary['orderId'] = o['orderId'];
    if (o['license_plate'] != null) summary['license_plate'] = o['license_plate'];
    if (o['vehicle_id'] != null) summary['vehicle_id'] = o['vehicle_id'];
    if (o['location_id'] != null) summary['location_id'] = o['location_id'];
    return summary;
  }

  ngOnInit(): void {
    this.initTunnelSessionReplayIfProd();
    this.pageOpenTime = Date.now();
    this.orderStatuses = this.ordersService.getOrderStatuses();
    this.logTunnelInteraction('lifecycle.ngOnInit');

    this.loadLocationFromProfile();
    this.refreshPaymentModeFilterOptions();

    this.ordersService.refreshOrders$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.logTunnelInteraction('orders.refreshPipe', { source: 'OrdersService.refreshOrders$' });
      if (this.loading && this.orders.length === 0) {
        return;
      }
      this.silentRefreshList();
    });

    this.registerTunnelResumeListeners();
  }

  ngOnDestroy(): void {
    this.logTunnelInteraction('lifecycle.ngOnDestroy');
    this.unregisterTunnelResumeListeners();
    if (this.resumeRefreshTimer !== null) {
      clearTimeout(this.resumeRefreshTimer);
      this.resumeRefreshTimer = null;
    }
    this.disconnectRealtime();
    this.stopTunnelWashClock();
    this.clearNonWebflowRetry();
    this.destroy$.next();
    this.destroy$.complete();
    this.rhombusImageCache.forEach(url => URL.revokeObjectURL(url));
    this.rhombusImageCache.clear();
    this.dismissDeleteUndoToast();
  }

  private clearNonWebflowRetry(): void {
    if (this.nonWebflowRetryTimer !== null) {
      clearTimeout(this.nonWebflowRetryTimer);
      this.nonWebflowRetryTimer = null;
    }
  }

  private registerTunnelResumeListeners(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    document.addEventListener('visibilitychange', this.onVisibilityChangeBound);
    window.addEventListener('online', this.onWindowOnlineBound);
  }

  private unregisterTunnelResumeListeners(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChangeBound);
    window.removeEventListener('online', this.onWindowOnlineBound);
  }

  private initTunnelSessionReplayIfProd(): void {
    if (environment.envName !== 'production') {
      return;
    }
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const w = window as Window & {
      amplitude?: {
        add?: (plugin: any) => void;
        init?: (apiKey: string, options?: Record<string, any>) => void;
      };
      sessionReplay?: { plugin?: (options?: { sampleRate?: number }) => any };
      __WAY_TUNNEL_SESSION_REPLAY_INITIALIZED__?: boolean;
    };

    const initAmplitude = () => {
      if (w.__WAY_TUNNEL_SESSION_REPLAY_INITIALIZED__) {
        return;
      }
      const add = w.amplitude?.add;
      const init = w.amplitude?.init;
      const plugin = w.sessionReplay?.plugin;
      if (!add || !init || !plugin) {
        return;
      }
      add(plugin({ sampleRate: 1 }));
      init(TunnelOrdersComponent.TUNNEL_AMPLITUDE_SESSION_REPLAY_API_KEY, {
        fetchRemoteConfig: true,
        autocapture: true,
      });
      w.__WAY_TUNNEL_SESSION_REPLAY_INITIALIZED__ = true;
      this.logTunnelInteraction('tunnel.sessionReplay.initialized');
    };

    if (w.amplitude && w.sessionReplay?.plugin) {
      initAmplitude();
      return;
    }

    const existing = document.getElementById(
      TunnelOrdersComponent.TUNNEL_AMPLITUDE_SCRIPT_ID
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', initAmplitude, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = TunnelOrdersComponent.TUNNEL_AMPLITUDE_SCRIPT_ID;
    script.src = TunnelOrdersComponent.TUNNEL_AMPLITUDE_SCRIPT_SRC;
    script.async = true;
    script.onload = initAmplitude;
    document.head.appendChild(script);
  }

  private onDocumentVisibilityChange(): void {
    if (typeof document === 'undefined') {
      return;
    }
    if (document.visibilityState === 'hidden') {
      this.visibilityHiddenAt = Date.now();
      return;
    }
    if (document.visibilityState !== 'visible') {
      return;
    }
    const hiddenAt = this.visibilityHiddenAt;
    this.visibilityHiddenAt = null;
    const elapsed = hiddenAt !== null ? Date.now() - hiddenAt : 0;
    if (hiddenAt !== null && elapsed >= this.minHiddenMsForResumeRefresh) {
      this.scheduleResumeRefresh('visibility');
    }
  }

  private onWindowOnline(): void {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    this.scheduleResumeRefresh('online');
  }

  /**
   * After sleep / app background or network restore: reconnect realtime and reload orders
   * so events missed while disconnected are reconciled from the API.
   */
  private scheduleResumeRefresh(reason: 'visibility' | 'online'): void {
    if (this.resumeRefreshTimer !== null) {
      clearTimeout(this.resumeRefreshTimer);
    }
    this.resumeRefreshTimer = setTimeout(() => {
      this.resumeRefreshTimer = null;
      this.logTunnelInteraction('lifecycle.resumeRefresh', { reason });
      this.initializeRealtime();
      if (this.loading && this.orders.length === 0) {
        this.resetAndLoad();
      } else {
        this.silentRefreshList();
      }
    }, this.resumeRefreshDebounceMs);
  }

  /**
   * The SSE stream re-established itself after a gap. Reload today's list so cars
   * pushed during the gap appear without waiting for the next car. Shares the
   * resume debounce timer so a flapping connection collapses into one request,
   * and keeps the attendant's current selection (fetch replaces the whole list,
   * so this cannot duplicate cards).
   */
  private scheduleReconnectRefresh(connectionId: string | null): void {
    if (this.resumeRefreshTimer !== null) {
      clearTimeout(this.resumeRefreshTimer);
    }
    this.resumeRefreshTimer = setTimeout(() => {
      this.resumeRefreshTimer = null;
      this.logTunnelInteraction('lifecycle.reconnectRefresh', { connectionId });
      this.silentRefreshList();
    }, this.resumeRefreshDebounceMs);
  }

  private loadLocationFromProfile(): void {
    try {
      const parsed = readActiveTunnelData();
      if (parsed) {
        this.selectedLocationId = String(
          parsed['locationId'] || parsed['location_id'] || parsed['location'] || parsed['id'] || ''
        ).trim();
        this.logTunnelInteraction('tunnelData.loaded', {
          hasRaw: true,
          locationId: this.selectedLocationId || null,
          impersonating: isThisTabImpersonating(),
          keys: Object.keys(parsed),
        });
      } else {
        this.logTunnelInteraction('tunnelData.loaded', { hasRaw: false, locationId: null });
      }
    } catch {
      this.selectedLocationId = '';
      this.logTunnelInteraction('tunnelData.loaded', { error: 'parse_failed', locationId: null });
    }
    this.loadTerminalPaymentFilterAvailability(this.selectedLocationId);
    this.resetAndLoad();
    this.initializeRealtime();
  }

  private initializeRealtime(): void {
    this.disconnectRealtime();
    this.initializeSse();
  }

  private disconnectRealtime(): void {
    this.disconnectSse();
  }

  private initializeSse(): void {
    this.logTunnelInteraction('sse.init.attempt', {
      locationId: this.selectedLocationId || null,
    });

    if (!this.selectedLocationId) {
      this.logger.warn(
        '[TunnelOrders] SSE skipped — no locationId',
        { snapshot: this.getTunnelSnapshot() },
        this.tunnelLogSource
      );
      return;
    }

    this.realtime.connectTunnel();
    this.ownsSseConnection = true;

    const sub = new Subscription();
    sub.add(
      this.realtime.on(VEHICLE_WASHES_SSE_EVENT).subscribe(data => {
        this.ngZone.run(() => this.handleVehicleWashEvent(data));
      })
    );
    sub.add(
      this.realtime.on(LPR_AXIS_ENTRY_UPDATED_SSE_EVENT).subscribe(data => {
        this.ngZone.run(() => this.handleLprAxisEntryUpdatedEvent(data));
      })
    );
    sub.add(
      this.realtime.on(TUNNEL_WASH_STATE_SSE_EVENT).subscribe(data => {
        this.ngZone.run(() => this.handleTunnelWashStateEvent(data));
      })
    );
    // svc-sse has no Last-Event-ID replay: a car pushed while the stream was dead
    // (liveness watchdog, session-expiry re-auth, network blip) is gone for this
    // client, and without this it only surfaces when the *next* car's event
    // triggers a refresh. Reconcile from the API every time the stream comes back.
    sub.add(
      this.realtime.ready$.subscribe(ready => {
        if (!ready.reconnect) {
          return;
        }
        this.ngZone.run(() => this.scheduleReconnectRefresh(ready.connectionId));
      })
    );
    // Catch-all: any SSE event with updated_pusher: true (backend field, not transport).
    sub.add(
      this.realtime.events$.subscribe(evt => {
        if (
          evt.type === VEHICLE_WASHES_SSE_EVENT ||
          evt.type === LPR_AXIS_ENTRY_UPDATED_SSE_EVENT ||
          evt.type === TUNNEL_WASH_STATE_SSE_EVENT
        ) {
          return;
        }
        if (!evt.data || typeof evt.data !== 'object') {
          return;
        }
        const payload = evt.data as Record<string, any>;
        if (!isUpdatedPusherPayload(payload)) {
          return;
        }
        this.ngZone.run(() => this.applyInPlaceTunnelLprPlateUpdate(payload));
      })
    );
    this.sseSub = sub;
    this.logTunnelInteraction('sse.init.subscribed', {
      locationId: this.selectedLocationId,
      events: [
        VEHICLE_WASHES_SSE_EVENT,
        LPR_AXIS_ENTRY_UPDATED_SSE_EVENT,
        TUNNEL_WASH_STATE_SSE_EVENT,
        'updated_pusher:*',
      ],
    });
  }

  private disconnectSse(): void {
    this.sseSub?.unsubscribe();
    this.sseSub = null;
    if (this.ownsSseConnection) {
      this.realtime.disconnect();
      this.ownsSseConnection = false;
      this.logTunnelInteraction('sse.disconnect', {
        locationId: this.selectedLocationId || null,
      });
    }
  }

  /**
   * CWS-1921: the Tunnel Controller state of a board row changed (pending → sent / failed,
   * or no tunnel package). Patch the row in place; a state for a row this list does not
   * have yet (e.g. it arrived while the stream was down) reloads the list.
   */
  private handleTunnelWashStateEvent(data: unknown): void {
    const event = parseTunnelWashStateEvent(data);
    if (!event) {
      return;
    }
    if (
      event.locationId &&
      this.selectedLocationId &&
      event.locationId !== this.selectedLocationId
    ) {
      return;
    }
    const index = this.orders.findIndex(
      order => order.lprId === event.lprEntryId || order.id === event.lprEntryId
    );
    this.logTunnelInteraction('tunnelWash.state', {
      lpr_entry_id: event.lprEntryId,
      status: event.status,
      tunnelPackageName: event.packageName,
      matched: index >= 0,
    });
    this.recentTunnelWashEvents = [
      ...this.recentTunnelWashEvents,
      { afterFetchSeq: this.fetchSeq, event },
    ].slice(-TunnelOrdersComponent.MAX_RECENT_TUNNEL_WASH_EVENTS);
    if (index < 0) {
      // Row not on the board (its arrival was missed, or the list is stale): reload.
      this.silentRefreshList();
      return;
    }
    const updated = applyTunnelWashState(this.orders[index], event, Date.now());
    this.orders = [...this.orders.slice(0, index), updated, ...this.orders.slice(index + 1)];
    if (isSameTunnelRow(this.selectedCardOrder, updated)) {
      this.setSelectedOrder(updated);
    }
    this.refreshTunnelWash();
  }

  /** The row's tunnel wash line (badge or notice); undefined when it has none. */
  tunnelWashRowFor(order: Order): TunnelWashRowDisplay | undefined {
    const key = tunnelRowKey(order);
    return key ? this.tunnelWashRows.get(key) : undefined;
  }

  /**
   * Re-evaluate the tunnel wash state against the clock: drop `sent` rows whose 30 s
   * linger is over, recompute every row's display, and run the 1 s clock only while a
   * row needs it.
   */
  private refreshTunnelWash(): void {
    this.nowMs = Date.now();
    this.removeExpiredTunnelRows();
    const rows = new Map<string, TunnelWashRowDisplay>();
    for (const order of this.orders) {
      const key = tunnelRowKey(order);
      const display = describeTunnelWashRow(order, this.nowMs);
      if (key && display) {
        rows.set(key, display);
      }
    }
    this.tunnelWashRows = rows;
    if (needsTunnelWashClock(this.orders)) {
      this.startTunnelWashClock();
    } else {
      this.stopTunnelWashClock();
    }
    this.cdr.markForCheck();
  }

  private removeExpiredTunnelRows(): void {
    const previous = this.orders;
    const isKept = (order: Order) => !isTunnelWashExpired(order, this.nowMs);
    const selectedIndex = previous.findIndex(o => isSameTunnelRow(o, this.selectedCardOrder));
    const kept = previous.filter(isKept);
    if (kept.length === previous.length) {
      return;
    }
    const selectionRemoved =
      selectedIndex >= 0 && !kept.some(o => isSameTunnelRow(o, this.selectedCardOrder));
    this.logTunnelInteraction('tunnelWash.lingerExpired', {
      removed: previous.length - kept.length,
    });
    this.orders = kept;
    if (selectionRemoved) {
      const next = indexAfterRemoval(previous, selectedIndex, isKept);
      this.setSelectedOrder(next >= 0 ? kept[next] : null);
    }
  }

  private startTunnelWashClock(): void {
    if (this.tunnelWashClock !== null) {
      return;
    }
    this.tunnelWashClock = setInterval(
      () => this.ngZone.run(() => this.refreshTunnelWash()),
      TunnelOrdersComponent.TUNNEL_WASH_CLOCK_MS
    );
  }

  private stopTunnelWashClock(): void {
    if (this.tunnelWashClock !== null) {
      clearInterval(this.tunnelWashClock);
      this.tunnelWashClock = null;
    }
  }

  /** Axis LPR row correction — same shape as NotificationService `lpr-axis-entry-updated`. */
  private handleLprAxisEntryUpdatedEvent(data: any): void {
    if (!data || typeof data !== 'object') {
      return;
    }
    const axisData = data as Record<string, any>;
    const isUpdated =
      axisData['Updated_event'] === true ||
      axisData['Updated_event'] === 'true' ||
      axisData['Updated_event'] === 1;
    if (!isUpdated) {
      return;
    }
    const lprEntryId = String(axisData['lpr_entry_id'] ?? '').trim();
    if (!lprEntryId) {
      return;
    }
    const row: Record<string, any> = {
      ...axisData,
      id: lprEntryId,
      lpr_entry_id: lprEntryId,
      updated_pusher: true,
    };
    this.applyInPlaceTunnelLprPlateUpdate(row);
  }

  /**
   * Silently patch the license plate on a matching tunnel list row when an LPR correction arrives.
   * No API reload — only the matched card is updated.
   */
  private applyInPlaceTunnelLprPlateUpdate(data: Record<string, any>): void {
    const incomingLoc = String(data['location_id'] ?? data['locationId'] ?? '').trim();
    if (incomingLoc && this.selectedLocationId && incomingLoc !== this.selectedLocationId) {
      return;
    }

    const index = this.findTunnelOrderIndexForLprUpdate(data);
    if (index < 0) {
      return;
    }

    const newPlate = this.resolvePlateFromLprPayload(data);
    if (!newPlate) {
      return;
    }

    const existing = this.orders[index];
    const prevPlate = (existing.licensePlate || '').toUpperCase();
    if (newPlate === prevPlate) {
      return;
    }

    const updated: Order = {
      ...existing,
      licensePlate: newPlate,
      vehicleInfo: this.updateVehicleInfoPlate(existing.vehicleInfo, newPlate),
    };

    this.orders = [...this.orders.slice(0, index), updated, ...this.orders.slice(index + 1)];
    this.cdr.markForCheck();

    if (isSameTunnelRow(this.selectedCardOrder, updated)) {
      this.setSelectedOrder(updated);
    }

    this.logTunnelInteraction('lpr.plate.updated', {
      lpr_entry_id: resolveLprEntryIdFromNotificationData(data),
      orderId: updated.orderId,
      rowId: updated.id,
      previousPlate: prevPlate,
      newPlate,
    });
  }

  private findTunnelOrderIndexForLprUpdate(data: Record<string, any>): number {
    const lprId = resolveLprEntryIdFromNotificationData(data);
    const orderId = String(data['order_id'] ?? data['orderId'] ?? '').trim();

    return this.orders.findIndex(order => {
      if (lprId && (order.lprId === lprId || order.id === lprId)) {
        return true;
      }
      if (orderId && (order.orderId === orderId || order.id === orderId)) {
        return true;
      }
      return false;
    });
  }

  private resolvePlateFromLprPayload(data: Record<string, any>): string {
    const displayPlate = String(data['display_license_plate'] ?? '').trim();
    if (displayPlate) {
      const event = String(data['event'] ?? '').trim();
      const state = String(data['state'] ?? data['lpr_state'] ?? '').trim();
      return this.normalizeFleetLicensePlate(
        displayPlate,
        event || undefined,
        state || undefined
      ).toUpperCase();
    }

    const payloadForDisplay = {
      ...data,
      state: data['state'] ?? data['lpr_state'] ?? '',
    };
    const stripped = getLicensePlateNumberFromNotification(payloadForDisplay);
    if (!stripped || stripped === 'Unknown') {
      return '';
    }
    const event = String(data['event'] ?? '').trim();
    const state = String(data['state'] ?? data['lpr_state'] ?? '').trim();
    return this.normalizeFleetLicensePlate(
      stripped,
      event || undefined,
      state || undefined
    ).toUpperCase();
  }

  private updateVehicleInfoPlate(vehicleInfo: string, newPlate: string): string {
    if (!newPlate) {
      return vehicleInfo;
    }
    if (vehicleInfo.includes('(')) {
      return vehicleInfo.replace(/\([^)]*\)\s*$/, `(${newPlate})`);
    }
    return `${vehicleInfo} (${newPlate})`.trim();
  }

  private normalizeFleetLicensePlate(plate: string, event?: string, stateCode?: string): string {
    if (!plate) return '';
    if (event !== 'fleet-vehicle-found') return plate;

    const cleanedPlate = String(plate).trim();
    const cleanedState = String(stateCode || '')
      .trim()
      .toUpperCase();

    if (cleanedState.length >= 2 && cleanedPlate.toUpperCase().startsWith(cleanedState)) {
      return cleanedPlate.slice(cleanedState.length).trim();
    }
    return cleanedPlate;
  }

  private handleVehicleWashEvent(data: any): void {
    if (!data) return;

    // Multiplexed SSE is not channel-scoped — ignore other locations if payload includes one.
    const incomingLoc = String(data.location_id ?? data.locationId ?? '').trim();
    if (incomingLoc && this.selectedLocationId && incomingLoc !== this.selectedLocationId) {
      return;
    }

    // CWS-1882: for a prepaid redemption `order_id` is the PURCHASE order, which may not be
    // a tunnel row at all — highlight it only when it is already on screen.
    const highlight = resolveVehicleWashHighlight(
      data,
      this.orders.flatMap(order => [order.id, order.orderId])
    );
    const incomingOrderId = highlight.highlightOrderId;
    this.newOrderId = incomingOrderId;
    if (incomingOrderId && data.paid_at) {
      const paidAtMs = Date.parse(String(data.paid_at));
      if (Number.isFinite(paidAtMs)) {
        this.vehicleWashTimingByOrderId.set(incomingOrderId, {
          paidAtMs,
          vehicleWashesReceivedAtMs: Date.now(),
        });
      }
    }
    this.logTunnelInteraction('vehicleWash.received', {
      orderId: highlight.incomingOrderId || null,
      prepaidRedemption: highlight.isPrepaidRedemption,
      highlighted: !!incomingOrderId,
      payload: this.summarizeRealtimePayload(data),
    });

    // Clear the "new" highlight after 4 seconds
    setTimeout(() => {
      if (this.newOrderId === incomingOrderId) {
        this.newOrderId = null;
      }
    }, 4000);

    // Silently refresh the list from the API so the real order data is shown (delayed 2s)
    setTimeout(() => this.silentRefreshList(incomingOrderId ?? undefined), 2000);
  }

  private reportSlowTunnelDisplayIfNeeded(
    orderId: string,
    displayedAt: number,
    plate?: string
  ): void {
    const timing = this.vehicleWashTimingByOrderId.get(orderId);
    if (!timing) return;

    this.vehicleWashTimingByOrderId.delete(orderId);

    const paidToDisplayMs = displayedAt - timing.paidAtMs;
    const elapsedMs = paidToDisplayMs - TunnelOrdersComponent.TUNNEL_LIST_REFRESH_DELAY_MS;
    if (elapsedMs <= TunnelOrdersComponent.SLOW_TUNNEL_DISPLAY_MS) return;

    this.apiService
      .postWithTunnelToken('order/tunnel-alert/slow-order-tunnel-display', {
        elapsedMs,
        paidToDisplayMs,
        vehicleWashesToDisplayMs: displayedAt - timing.vehicleWashesReceivedAtMs,
        orderId,
        locationId: this.selectedLocationId || null,
        plate: plate ?? null,
      })
      .subscribe();
  }

  /**
   * Refresh today's orders without clearing the list or showing the full-page shimmer.
   * Used for SSE vehicle_washes events, tab resume, and OrdersService.refreshOrders$.
   */
  private silentRefreshList(highlightOrderId?: string): void {
    if (this.loading && this.orders.length === 0) {
      return;
    }
    this.fetchOrders({ silent: true, highlightOrderId, preserveSelection: true });
  }

  private resetAndLoad(): void {
    this.logTunnelInteraction('orders.resetAndLoad');

    const silent = this.orders.length > 0;
    if (!silent) {
      this.orders = [];
      this.setSelectedOrder(null);
    }

    this.fetchOrders({ silent, preserveSelection: !silent });
  }

  logout(): void {
    this.logTunnelInteraction('auth.logout');
    if (isThisTabImpersonating()) {
      this.impersonation.exitImpersonation();
      return;
    }
    localStorage.removeItem('tunnelData');
    this.router.navigate(['/tunnel/login']);
  }

  private fetchOrders(options?: {
    silent?: boolean;
    highlightOrderId?: string;
    preserveSelection?: boolean;
  }): void {
    const silent = options?.silent === true;

    if (!silent && this.orders.length === 0) {
      this.loading = true;
    }

    const params: Record<string, any> = {
      filter_by: 'today',
    };

    if (this.searchTerm?.trim()) {
      params['search'] = this.searchTerm.trim();
    }

    const seq = ++this.fetchSeq;
    this.logTunnelInteraction('orders.fetch.start', { silent, params, seq });

    this.apiService
      .getWithTunnelToken('location/get-all-orders', params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (seq < this.appliedFetchSeq) {
            this.logTunnelInteraction('orders.fetch.staleIgnored', {
              seq,
              appliedSeq: this.appliedFetchSeq,
            });
            return;
          }
          this.appliedFetchSeq = seq;
          const rawOrders: any[] = response?.data?.orders ?? [];
          // The API returns sent rows for 30 s after completion; one older than that
          // (slow response, clock skew) is dropped rather than flashed (CWS-1921).
          const fetchedAtMs = Date.now();
          const mapped = reapplyTunnelWashEvents(
            this.mapApiOrders(rawOrders),
            this.recentTunnelWashEvents,
            seq,
            fetchedAtMs
          ).filter(order => !isTunnelWashExpired(order, fetchedAtMs));
          this.recentTunnelWashEvents = this.recentTunnelWashEvents.filter(
            entry => entry.afterFetchSeq >= seq
          );
          this.orders = mapped;

          // Tiles of one membership share an order id, so remember the tile itself
          // (CWS-1231). Read at response time, not request time — a key captured at
          // request start would overwrite an operator click made while the fetch was
          // in flight (CWS-1375 review).
          const previousSelectionKey = tunnelRowKey(this.selectedCardOrder);

          const highlightId = options?.highlightOrderId;
          if (highlightId) {
            const highlighted = mapped.find(o => o.id === highlightId || o.orderId === highlightId);
            if (highlighted) {
              // CWS-1375: an arrival must not steal a selection the operator already
              // has — with two lanes feeding one list, the detail panel was silently
              // switching to the other lane's vehicle. Auto-select the arrival only
              // when nothing is selected or the selection left the list; otherwise
              // keep the operator's row and let the arrival flash via isNewTile.
              const stillSelected =
                options?.preserveSelection !== false && previousSelectionKey
                  ? mapped.find(o => tunnelRowKey(o) === previousSelectionKey)
                  : undefined;
              if (stillSelected) {
                this.setSelectedOrder(stillSelected, 'operator-kept');
                this.logTunnelInteraction('selection.arrivalSuppressed', {
                  arrivalOrderId: highlightId,
                  keptRowId: previousSelectionKey,
                });
              } else {
                this.setSelectedOrder(highlighted, 'arrival');
              }
              this.reportSlowTunnelDisplayIfNeeded(
                highlightId,
                Date.now(),
                highlighted.licensePlate
              );
            } else {
              this.vehicleWashTimingByOrderId.delete(highlightId);
              if (options?.preserveSelection !== false && previousSelectionKey) {
                const stillSelected = mapped.find(o => tunnelRowKey(o) === previousSelectionKey);
                this.setSelectedOrder(stillSelected ?? mapped[0] ?? null);
              } else {
                this.setSelectedOrder(mapped.length > 0 ? mapped[0] : null);
              }
            }
          } else if (options?.preserveSelection !== false && previousSelectionKey) {
            const stillSelected = mapped.find(o => tunnelRowKey(o) === previousSelectionKey);
            this.setSelectedOrder(stillSelected ?? mapped[0] ?? null);
          } else if (!silent || !previousSelectionKey) {
            this.setSelectedOrder(mapped.length > 0 ? mapped[0] : null);
          }

          this.loading = false;
          this.refreshTunnelWash();

          this.logTunnelInteraction('orders.fetch.success', {
            silent,
            rawCount: rawOrders.length,
            ordersCount: this.orders.length,
          });
        },
        error: (err: any) => {
          this.loading = false;
          this.cdr.markForCheck();

          const status = err?.status || err?.error?.status_code;
          const unauthorized = status === 401 || status === 403;
          this.logTunnelInteraction('orders.fetch.error', {
            httpStatus: status,
            unauthorized,
            message: err?.message,
          });
          if (unauthorized) {
            if (isThisTabImpersonating()) {
              removeImpersonationStorageItem(IMPERSONATION_TUNNEL_DATA_KEY);
            } else {
              localStorage.removeItem('tunnelData');
            }
            this.router.navigate(['/tunnel/login']);
          }
        },
      });
  }

  private mapApiOrders(apiOrders: any[]): Order[] {
    return apiOrders.map(o => {
      const normalizedOrderId = o.order_id || o.lpr_entry_id || o.id || '';
      const normalizedRowId = o.id || o.lpr_entry_id || normalizedOrderId;
      const normalizedDateTime = o.created_at_local
        ? formatLocalDateTime(o.created_at_local)
        : o.appointment_time_local
          ? formatLocalDateTime(o.appointment_time_local)
          : formatDateTimeWithTimezone(o.created_at_utc || o.appointment_time || '');
      const normalizedWashType =
        o.wash_type ||
        (o.event === 'fleet-vehicle-found'
          ? o.fleet_name
            ? `${o.fleet_name} (Fleet)`
            : 'Fleet'
          : '');
      const mappedVehicle = OrderUtils.mapVehicleFromApi({
        make: o.make,
        model: o.model,
        license_plate: o.license_plate || o.plate,
        display_license_plate: o.display_license_plate,
        fleet_license_plate: o.fleet_license_plate,
        fleet_vehicle_type: o.fleet_vehicle_type,
        fleet_id: o.fleet_id,
        customer_type: o.customer_type,
      });
      let normalizedLicensePlate = mappedVehicle.licensePlate;
      if (o.event === 'fleet-vehicle-found' && normalizedLicensePlate) {
        normalizedLicensePlate = this.normalizeFleetLicensePlate(
          normalizedLicensePlate,
          o.event,
          o.lpr_state
        );
      }
      let normalizedVehicleInfo = mappedVehicle.vehicleInfo;
      if (normalizedLicensePlate && normalizedVehicleInfo.includes('(')) {
        normalizedVehicleInfo = normalizedVehicleInfo.replace(
          /\([^)]*\)\s*$/,
          `(${normalizedLicensePlate})`
        );
      } else if (normalizedLicensePlate) {
        normalizedVehicleInfo = `${normalizedVehicleInfo} (${normalizedLicensePlate})`.trim();
      }

      let washCount: string | undefined;
      const formattedWashCount = OrderUtils.formatMembershipWashCountDisplay(o);
      if (formattedWashCount !== 'N/A') {
        washCount = formattedWashCount;
      }

      return {
        id: normalizedRowId,
        customerId: o.customer_id,
        orderId: normalizedOrderId,
        status: this.mapOrderStatus(o.order_status),
        dateTime: normalizedDateTime,
        customerName: o.customer_name || o.fleet_name || '',
        userName: o.user_name,
        userId: o.user_id,
        washType: OrderUtils.formatWashType(normalizedWashType),
        washTypeValue: normalizedWashType,
        addOnLabels: OrderUtils.formatAddOnKindLabels(o.addon_kinds),
        email: o.customer_email || '',
        phone: o.customer_contact || '',
        price: `$${o.final_price || '0.00'}`,
        listPrice: OrderUtils.formatListPriceDisplay(o),
        voucherCode: o.voucher_code || undefined,
        discount: `$${o.discount || '0.00'}`,
        source: o.source || '',
        vehicleInfo: normalizedVehicleInfo,
        barCode: normalizedRowId,
        licensePlate: normalizedLicensePlate,
        vehicleId: o.vehicle_id,
        branch: o.branch,
        packageName: o.package_name,
        isRewash: o.is_rewash === true || OrderUtils.isRewashWashType(normalizedWashType),
        payment_method: o.payment_method || undefined,
        vin: o.vin || undefined,
        vinNumber: o.vin || undefined,
        lprId: o.lpr_entry_id || undefined,
        // Prefer vehicle overview; fall back to plate crop (UI image-scan / webflow).
        imageUrl: o.image_s3_url || o.plate_image_s3_url || null,
        imageSource: o.image_source || null,
        deviceUuid: o.device_uuid || null,
        triggeredAt: o.triggered_at ?? null,
        rhombusFrameUri: o.rhombus_frame_uri ?? null,
        event: o.event || undefined,
        fleetId: o.fleet_id || null,
        fleetName: o.fleet_name || null,
        fleetVehicleType: o.fleet_vehicle_type || null,
        washCount,
        ...mapTunnelWashFieldsFromApi(o),
      };
    });
  }

  private mapOrderStatus(orderStatus: string): OrderStatus {
    switch (orderStatus) {
      case 'completed':
        return { label: 'Completed', value: 'completed', color: 'green' };
      case 'scheduled':
        return { label: 'Scheduled', value: 'scheduled', color: 'blue' };
      case 'cancelled':
        return { label: 'Canceled', value: 'canceled', color: 'red' };
      case 'inprogress':
        return { label: 'In Progress', value: 'in_progress', color: 'in-progress' };
      default:
        return { label: 'Pending', value: 'pending', color: 'gray' };
    }
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.logTunnelInteraction('search.change', {
      length: searchTerm?.length ?? 0,
      preview: (searchTerm || '').trim().slice(0, 80) || null,
    });
    this.resetAndLoad();
  }

  onFilter(): void {
    this.logTunnelInteraction('filters.modal.open');
    this.showFilterModal = true;
    this.resetFilterCategoriesToAppliedState();
  }

  onFilterModalApplyShared(categories: FilterCategory[]): void {
    this.appliedFilters = this.convertCategoriesToOrderFilters(categories);
    this.showFilterModal = false;
    this.logTunnelInteraction('filters.apply', { applied: this.appliedFilters });
    this.resetAndLoad();
  }

  onCloseFilterModal(): void {
    this.showFilterModal = false;
    this.logTunnelInteraction('filters.modal.close');
    this.resetFilterCategoriesToAppliedState();
  }

  selectCard(order: Order): void {
    this.setSelectedOrder(order, 'operator');
  }

  /** Whether this tile is the selected one; tiles of one membership share an order id. */
  isSelectedTile(order: Order): boolean {
    return isSameTunnelRow(this.selectedCardOrder, order);
  }

  /** Whether this tile is the wash that just arrived (the newest tile for that order). */
  isNewTile(order: Order): boolean {
    if (!this.newOrderId || order.id !== this.newOrderId) {
      return false;
    }
    return isSameTunnelRow(findLatestTunnelRowForOrder(this.orders, this.newOrderId), order);
  }

  // CWS-1375: `cause` makes silent panel switches diagnosable from telemetry —
  // 'operator' (explicit click), 'arrival' (auto-select from an SSE arrival),
  // 'operator-kept' (arrival suppressed, operator's row re-resolved), or
  // 'programmatic' (refresh fallbacks, deletes, plate edits).
  private setSelectedOrder(order: Order | null, cause = 'programmatic'): void {
    const prevSelId = tunnelRowKey(this.selectedCardOrder);
    const nextSelId = tunnelRowKey(order);
    if (prevSelId !== nextSelId) {
      this.logTunnelInteraction('selection.change', {
        previousRowId: prevSelId,
        nextRowId: nextSelId,
        orderId: order?.orderId ?? null,
        cause,
      });
    }
    this.selectedCardOrder = order;
    this.cdr.markForCheck();
  }

  /** Called by (load) on the non-webflow img element. */
  onNonWebflowImageLoaded(orderId: string | undefined): void {
    if (orderId && orderId === this.nonWebflowImageOrderId) {
      this.clearNonWebflowRetry();
      this.nonWebflowDetailImageLoading = false;
    }
  }

  /**
   * Called by (error) on the non-webflow img element.
   * If a URL exists, retries silently after NONWEBFLOW_RETRY_INTERVAL_MS
   * instead of showing an error — image may still be processing on the server.
   * "Failed to load image" / "Image not available" is only shown when no URL exists.
   */
  onNonWebflowImageError(orderId: string | undefined): void {
    if (!orderId || orderId !== this.nonWebflowImageOrderId) {
      return; // stale event from a previous order — discard
    }

    const retryUrl = this.nonWebflowImageRetryUrl;
    if (!retryUrl) {
      // No URL to retry — nothing we can do
      this.nonWebflowDetailImageLoading = false;
      return;
    }

    // URL exists but load failed (image may still be processing).
    // Hide the img element so the browser drops the failed request, keep
    // the loading spinner visible, then re-inject the URL after a delay.
    this.nonWebflowDetailImageUrl = null;
    this.nonWebflowDetailImageLoading = true;

    this.nonWebflowRetryTimer = setTimeout(() => {
      // Only retry if the user is still viewing the same order
      if (this.nonWebflowImageOrderId === orderId) {
        this.nonWebflowDetailImageUrl = retryUrl;
      }
    }, this.NONWEBFLOW_RETRY_INTERVAL_MS);
  }

  /**
   * Tunnel detail image: API may send `image_source` `axis` (current) or legacy `rhombus`
   * for the authenticated frame URL in `rhombus_frame_uri`.
   */
  isAxisStreamImageSource(order: Order | null): boolean {
    const s = order?.imageSource;
    return s === 'axis' || s === 'rhombus';
  }

  /** Rhombus/Axis frame fetch only when the API provides `rhombus_frame_uri`; otherwise use `image_s3_url`. */
  usesRhombusFrameFetch(order: Order | null): boolean {
    return this.isAxisStreamImageSource(order) && !!order?.rhombusFrameUri;
  }

  /** Fetches Rhombus frame bytes using `rhombus_frame_uri` from the API plus Rhombus API key. */
  private loadRhombusImage(order: Order): void {
    const mediaUrl = order.rhombusFrameUri;
    if (!mediaUrl) {
      return;
    }

    // Per tile: tiles of one membership share an order id but each has its own frame (CWS-1231).
    const cacheKey = tunnelRowKey(order) ?? order.id;
    if (this.rhombusImageCache.has(cacheKey)) {
      this.rhombusImageUrl = this.rhombusImageCache.get(cacheKey) || null;
      this.logTunnelInteraction('detailImage.rhombus.cacheHit', { orderRowId: order.id });
      return;
    }

    this.logTunnelInteraction('detailImage.rhombus.fetch.start', { orderRowId: order.id });
    this.rhombusImageLoading = true;
    this.rhombusImageError = false;

    const headers = new HttpHeaders({
      'x-auth-scheme': 'api-token',
      'x-auth-apikey': this.RHOMBUS_API_KEY,
      Accept: 'application/json',
    });

    this.http.get(mediaUrl, { headers, responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        const blobUrl = URL.createObjectURL(blob);
        this.rhombusImageCache.set(cacheKey, blobUrl);
        if (isSameTunnelRow(this.selectedCardOrder, order)) {
          this.rhombusImageUrl = blobUrl;
        }
        this.rhombusImageLoading = false;
        this.cdr.markForCheck();
        this.logTunnelInteraction('detailImage.rhombus.fetch.success', {
          orderRowId: order.id,
          blobSize: blob?.size ?? 0,
        });
      },
      error: () => {
        this.rhombusImageLoading = false;
        if (isSameTunnelRow(this.selectedCardOrder, order)) {
          this.rhombusImageError = true;
        }
        this.cdr.markForCheck();
        this.logTunnelInteraction('detailImage.rhombus.fetch.error', { orderRowId: order.id });
      },
    });
  }

  openOrderDetails(order: Order): void {
    this.logTunnelInteraction('orderDetails.open', { orderId: order.orderId, rowId: order.id });
    this.selectedOrder = order;
    this.showOrderDetailsModal = true;
  }

  closeOrderDetails(): void {
    this.logTunnelInteraction('orderDetails.close', {
      orderId: this.selectedOrder?.orderId ?? null,
    });
    this.showOrderDetailsModal = false;
    this.selectedOrder = null;
  }

  onOrderCancelled(_order?: Order): void {
    this.logTunnelInteraction('orderDetails.orderCancelled.refresh');
    this.resetAndLoad();
  }

  deleteOrder(order: Order, event: Event): void {
    event.stopPropagation();
    if (!order.lprId?.trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Delete Failed',
        detail: 'This order cannot be deleted.',
      });
      return;
    }
    if (this.isDeleteApiInFlight) {
      return;
    }

    if (this.pendingDelete && !isSameTunnelRow(this.pendingDelete.order, order)) {
      this.dismissDeleteUndoToast();
    }
    if (isSameTunnelRow(this.pendingDelete?.order, order)) {
      return;
    }

    const index = this.orders.findIndex(o => isSameTunnelRow(o, order));
    if (index < 0) {
      return;
    }

    this.logTunnelInteraction('delete.api.start', {
      rowId: order.id,
      orderId: order.orderId,
      lprId: order.lprId,
      delete: true,
    });

    const [removed] = this.orders.splice(index, 1);
    if (isSameTunnelRow(this.selectedCardOrder, removed)) {
      this.setSelectedOrder(
        this.orders.length > 0 ? this.orders[Math.min(index, this.orders.length - 1)] : null
      );
    }
    this.refreshTunnelWash();

    this.isDeleteApiInFlight = true;
    this.setTunnelOrderDeletionStatus(removed, true).subscribe({
      next: () => {
        this.isDeleteApiInFlight = false;
        this.logTunnelInteraction('delete.success', {
          rowId: removed.id,
          orderId: removed.orderId,
          delete: true,
        });
        const undoTimeoutId = setTimeout(
          () => this.dismissDeleteUndoToast(),
          TunnelOrdersComponent.DELETE_UNDO_MS
        );
        this.pendingDelete = { order: removed, index, undoTimeoutId };
        this.deleteUndoToast = { licensePlate: removed.licensePlate || 'Order' };
        this.cdr.markForCheck();
      },
      error: () => {
        this.isDeleteApiInFlight = false;
        this.logTunnelInteraction('delete.error', {
          rowId: removed.id,
          orderId: removed.orderId,
          delete: true,
        });
        const insertAt = Math.min(Math.max(index, 0), this.orders.length);
        this.orders.splice(insertAt, 0, removed);
        this.setSelectedOrder(removed);
        this.refreshTunnelWash();
        this.messageService.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: 'Failed to delete the order. Please try again.',
        });
      },
    });
  }

  undoDeleteOrder(): void {
    const pending = this.pendingDelete;
    if (!pending || this.isDeleteApiInFlight) {
      return;
    }

    const { order, index } = pending;
    this.dismissDeleteUndoToast();
    this.logTunnelInteraction('delete.undo.api.start', {
      rowId: order.id,
      orderId: order.orderId,
      lprId: order.lprId,
      delete: false,
    });

    this.isDeleteApiInFlight = true;
    this.setTunnelOrderDeletionStatus(order, false).subscribe({
      next: () => {
        this.isDeleteApiInFlight = false;
        this.logTunnelInteraction('delete.undo.success', {
          rowId: order.id,
          orderId: order.orderId,
          delete: false,
        });
        const insertAt = Math.min(Math.max(index, 0), this.orders.length);
        this.orders.splice(insertAt, 0, order);
        this.setSelectedOrder(order);
        this.refreshTunnelWash();
      },
      error: () => {
        this.isDeleteApiInFlight = false;
        this.logTunnelInteraction('delete.undo.error', {
          rowId: order.id,
          orderId: order.orderId,
          delete: false,
        });
        this.cdr.markForCheck();
        this.messageService.add({
          severity: 'error',
          summary: 'Undo Failed',
          detail: 'Could not restore the order. Please try again.',
        });
      },
    });
  }

  /** PUT location/order/update/:id with { tunnel_deletion_status }. */
  private setTunnelOrderDeletionStatus(order: Order, deleted: boolean) {
    return this.apiService.putWithTunnelToken(`location/order/update/${order.lprId}`, {
      tunnel_deletion_status: deleted,
    });
  }

  private dismissDeleteUndoToast(): void {
    if (this.pendingDelete) {
      clearTimeout(this.pendingDelete.undoTimeoutId);
    }
    this.pendingDelete = null;
    this.deleteUndoToast = null;
    this.cdr.markForCheck();
  }

  getTunnelPackageTitle(order: Order | null): string {
    if (!order) return '-';
    const packageName = order.packageName?.trim() || '-';
    return order.isRewash ? `${packageName} - Rewash` : packageName;
  }

  getMakeModel(order: Order): string {
    if (order.event === 'fleet-vehicle-found') {
      const fleetType = (order.fleetVehicleType || '').trim();
      if (fleetType) {
        return fleetType.toUpperCase();
      }
      return 'Fleet';
    }

    if (!order.vehicleInfo) return 'Vehicle make not available';
    // vehicleInfo format: "Make Model (LicensePlate)"
    const withoutPlate = order.vehicleInfo
      .replace(`(${order.licensePlate})`, '')
      .replace(` (${order.licensePlate})`, '')
      .trim();
    if (
      !withoutPlate ||
      withoutPlate === 'null null' ||
      withoutPlate === 'null' ||
      withoutPlate === order.licensePlate ||
      withoutPlate === `(${order.licensePlate})`
    ) {
      return 'Vehicle make not available';
    }
    return withoutPlate;
  }

  getLicensePlateParts(plate: string): { prefix: string; suffix: string } {
    if (!plate || plate.length <= 3) return { prefix: '', suffix: plate || '—' };
    return {
      prefix: plate.slice(0, -3),
      suffix: plate.slice(-3),
    };
  }

  getStatusClass(status: OrderStatus): string {
    return this.ordersService.getStatusClass(status);
  }

  getWashTypeClass(washType: string, washTypeValue?: string): string {
    return this.ordersService.getWashTypeClass(washType, washTypeValue);
  }

  /** Chip colour for an add-on label shown beside the wash type (CWS-1125). */
  getAddOnLabelClass(label: string): string {
    return OrderUtils.getAddOnLabelClass(label);
  }

  getCapitalizedSource(source: string | undefined): string {
    return OrderUtils.capitalizeSource(source);
  }

  formatPaymentMethod(paymentMethod: string | undefined): string {
    if (!paymentMethod) return '—';
    if (paymentMethod.toLowerCase() === PaymentMethodCode.CardPresent) return 'Card';
    return paymentMethod
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  get hasActiveFilters(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  get hasSearchOrFilters(): boolean {
    return (!!this.searchTerm && this.searchTerm.trim().length > 0) || this.hasActiveFilters;
  }

  getActiveFilterCount(): number {
    return countActiveOrderFilters(this.appliedFilters);
  }

  private getDefaultFilters(): OrderFilters {
    return createDefaultAdminOrderFilters();
  }

  private convertCategoriesToOrderFilters(categories: FilterCategory[]): OrderFilters {
    return convertSharedCategoriesToOrderFilters(categories, this.getDefaultFilters());
  }

  private resetFilterCategoriesToAppliedState(): void {
    this.filterCategories.update(cats =>
      cats.map(cat => ({
        ...cat,
        options: cat.options.map(opt => ({
          ...opt,
          selected: this.isOptionApplied(cat.id, opt.value),
        })),
      }))
    );
    this.refreshPaymentModeFilterOptions();
  }

  private refreshPaymentModeFilterOptions(): void {
    this.filterCategories.update(categories =>
      syncOrderPaymentModeFilterCategories(
        categories,
        this.filterPaymentModes,
        this.appliedFilters.paymentMode,
        this.showTerminalPaymentFilter
      )
    );
  }

  private loadTerminalPaymentFilterAvailability(locationId: string): void {
    // Terminal reader admin API requires shop user auth, not tunnel location JWT.
    this.setTerminalPaymentFilterVisible(false);
  }

  private setTerminalPaymentFilterVisible(visible: boolean): void {
    const hadTerminalFilter = !!this.appliedFilters.paymentMode?.terminal;
    const next = applyOrderTerminalPaymentFilterVisibility(visible, this.appliedFilters);
    this.showTerminalPaymentFilter = next.showTerminalPaymentFilter;
    this.appliedFilters = next.appliedFilters;
    this.refreshPaymentModeFilterOptions();
    if (hadTerminalFilter && !visible) {
      this.loadOrders();
    }
  }

  private isOptionApplied(categoryId: string, optionValue: string): boolean {
    return isOptionAppliedFromOrderFilters(this.appliedFilters, categoryId, optionValue);
  }

  loadOrders(): void {
    this.logTunnelInteraction('orders.load.manual');
    this.resetAndLoad();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
